import LFCDataGridSimple from '_components/datagrid/LFCDataGridSimple';
import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import {ProgressAria} from '_components/ProgressAria';
import {fmtYYYYMMDDHHmmss} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import dayjs from 'dayjs';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import DescriptionIcon from '@mui/icons-material/Description';
import {Box, Button, Dialog, DialogContent, Divider} from '@mui/material';
import {GridValueFormatterParams, GridValueGetterParams} from '@mui/x-data-grid';

import {CloudDistanceMaxAnalysis} from './components/CloudDistanceMaxAnalysis';
import {CloudDistanceMaxMinComparison} from './components/CloudDistanceMaxMinComparison';
import {CloudDistanceMinAnalysis} from './components/CloudDistanceMinAnalysis';
import {CloudDistanceDigGraphBaseData, SearchConditions} from './types';

// 初期値を返す関数として定義（毎回新しいオブジェクトを生成）
const getGraphBaseDataInit = () => ({
  data_upper_distance: [],
  data_lower_distance: [],
  data_upper_limits: [],
  data_lower_limits: [],
  upper_distance_min: 0,
  upper_distance_max: 0,
  lower_distance_min: 0,
  lower_distance_max: 0,
  limits_min: 0,
  limits_max: 0,
  hasMultipleLimit: false,
  xlist: [],
  detail: [], // listDataの行情報を持つ
  map: ''
});

interface MainPanelProps {
  searchConditions: SearchConditions | null;
}

export const MainPanel = ({searchConditions}: MainPanelProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const {parseSize} = useParseWithSettings();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [listData, setListData] = useState<any>([]); //sql_id: 30003
  const [graphBaseData, setGraphBaseData] =
    useState<CloudDistanceDigGraphBaseData>(getGraphBaseDataInit());

  const CloudDistanceDetailColumn: {
    field: string;
    headerName: string;
    description: string;
    sortable: boolean;
    width: number;
    valueFormatter?: (prms: GridValueFormatterParams) => string | undefined;
    valueGetter?: (prms: GridValueGetterParams) => any;
  }[] = [
    {
      field: 'id',
      headerName: t('連番'),
      description: t('連番'),
      sortable: false,
      width: 100
    },
    {
      field: 'serial',
      headerName: t('シリアルNo.'),
      description: t('ワークごとの識別子'),
      sortable: false,
      width: 200,
      valueGetter: (prms: GridValueGetterParams) => {
        // serialが未設定（null、undefined、空文字）の場合はuuidを表示
        if (!prms.row.serial || prms.row.serial === '') {
          return prms.row.uuid || '';
        }
        return prms.row.serial;
      }
    },
    {
      field: 'map',
      headerName: t('検査部位'),
      description: t('検査部位の識別子'),
      width: 100,
      sortable: false
    },
    {
      field: 'lq_time',
      headerName: t('検査日時'),
      description: t('LQを実施した日時'),
      width: 200,
      sortable: false,
      valueFormatter: (prms: GridValueFormatterParams) => fmtYYYYMMDDHHmmss(prms.value)
    },
    {
      field: 'upper_distance',
      headerName: t('距離 最大値'),
      description: t('距離 最大値'),
      width: 200,
      sortable: false,
      valueFormatter: (prms: GridValueFormatterParams) => parseSize(prms.value).toString()
    },
    {
      field: 'lower_distance',
      headerName: t('距離 最小値'),
      description: t('距離 最小値'),
      width: 200,
      sortable: false,
      valueFormatter: (prms: GridValueFormatterParams) => parseSize(prms.value).toString()
    },
    {
      field: 'upper_limit',
      headerName: t('上限'),
      description: t('上限'),
      width: 200,
      sortable: false,
      valueFormatter: (prms: GridValueFormatterParams) => parseSize(prms.value).toString()
    },
    {
      field: 'lower_limit',
      headerName: t('下限'),
      description: t('下限'),
      width: 200,
      sortable: false,
      valueFormatter: (prms: GridValueFormatterParams) => parseSize(prms.value).toString()
    }
  ];

  const handleClose = () => {
    setDialogOpen(false);
  };

  /***
   * X軸生成処理
   */
  const dateCalc = useCallback(() => {
    if (!searchConditions) return;

    let datelist: any = [];
    let from_day = new Date(searchConditions.selectDateFrom);
    let to_day = new Date(searchConditions.selectDateTo);

    switch (searchConditions.searchType) {
      case 0:
        return [];
      case 1:
        for (let d = from_day; d <= to_day; d.setHours(d.getHours() + 1)) {
          datelist.push(dayjs(d).format('YYYY-MM-DD HH'));
        }
        return datelist.sort();
      case 2:
        for (let d = from_day; d <= to_day; d.setDate(d.getDate() + 1)) {
          datelist.push(dayjs(d).format('YYYY-MM-DD'));
        }
        return datelist.sort();
      case 3:
        let start_month_1day: any = new Date(from_day.setDate(1));
        let end_month_1day: any = new Date(to_day.setDate(1));
        for (
          start_month_1day;
          start_month_1day <= end_month_1day;
          start_month_1day.setMonth(start_month_1day.getMonth() + 1)
        ) {
          datelist.push(dayjs(start_month_1day).format('YYYY-MM'));
        }
        return datelist.sort();
    }
  }, [
    searchConditions?.selectDateFrom,
    searchConditions?.selectDateTo,
    searchConditions?.searchType
  ]);

  useEffect(() => {
    if (!searchConditions) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 30003,
        parameters: {
          select_date_from: searchConditions.selectDateFrom,
          select_date_to: searchConditions.selectDateTo,
          work: !isEmpty(searchConditions.work)
            ? '{' + searchConditions.work.join(',') + '}'
            : null,
          deviceid: !isEmpty(searchConditions.deviceid)
            ? '{' + searchConditions.deviceid.join(',') + '}'
            : null,
          judge: searchConditions.judge === 9 ? null : searchConditions.judge,
          map: searchConditions.map
        },
        name: 'id-30003',
        cancelToken: cancelSource.current.token,
        t
      }).then(data => {
        console.log('getLFCData 30003 data', data);
        setListData(
          data.map((e: any) => {
            return {
              ...e,
              id: e.idx
            };
          })
        );
      })
    ]).finally(() => {
      setStartProcess(false);
    });
  }, [searchConditions]);

  /**
   * チャートデータ生成処理
   */
  useEffect(() => {
    if (!searchConditions) return;

    // 新しいオブジェクトを作成して初期化
    const tmp_all: CloudDistanceDigGraphBaseData = getGraphBaseDataInit();

    if (listData.length === 0) {
      setGraphBaseData(tmp_all);
      return;
    }

    if (searchConditions.searchType === 0) {
      //検査毎
      listData.forEach((r: any) => {
        tmp_all.xlist.push(r.idx);
        tmp_all.detail.push(r);
        //検査毎の場合だけ数値が直に入ってる
        tmp_all.data_upper_distance.push(Number(r.upper_distance));
        tmp_all.data_lower_distance.push(Number(r.lower_distance));
        tmp_all.data_upper_limits.push(Number(r.upper_limit));
        tmp_all.data_lower_limits.push(Number(r.lower_limit));
      });
      tmp_all.upper_distance_min = Math.min(...(tmp_all.data_upper_distance as Array<number>));
      tmp_all.upper_distance_max = Math.max(...(tmp_all.data_upper_distance as Array<number>));
      tmp_all.lower_distance_min = Math.min(...(tmp_all.data_lower_distance as Array<number>));
      tmp_all.lower_distance_max = Math.max(...(tmp_all.data_lower_distance as Array<number>));
      tmp_all.limits_min = Math.min(...(tmp_all.data_lower_limits as Array<number>));
      tmp_all.limits_max = Math.max(...(tmp_all.data_upper_limits as Array<number>));
    } else {
      dateCalc().forEach((lq_time_string: string) => {
        let tmp_upper_distance: number[] = [];
        let tmp_lower_distance: number[] = [];
        let tmp_upper_limits: number[] = [];
        let tmp_lower_limits: number[] = [];
        let tval = 0;
        listData.forEach((r: any) => {
          if (
            (searchConditions?.searchType === 1 && r.year_month_day_hour === lq_time_string) ||
            (searchConditions?.searchType === 2 && r.year_month_day === lq_time_string) ||
            (searchConditions?.searchType === 3 && r.year_month === lq_time_string)
          ) {
            tmp_upper_distance.push(Number(r.upper_distance));
            tmp_lower_distance.push(Number(r.lower_distance));
            tmp_upper_limits.push(Number(r.upper_limit));
            tmp_lower_limits.push(Number(r.lower_limit));
          }
        });
        tmp_all.xlist.push(lq_time_string);
        tmp_all.detail.push([]);
        tmp_all.data_upper_distance.push(tmp_upper_distance);
        tmp_all.data_lower_distance.push(tmp_lower_distance);
        tmp_all.data_upper_limits.push(tmp_upper_limits);
        tmp_all.data_lower_limits.push(tmp_lower_limits);

        tval = Math.min(...tmp_upper_distance);
        if (tval < tmp_all.upper_distance_min) {
          tmp_all.upper_distance_min = tval;
        }
        tval = Math.max(...tmp_upper_distance);
        if (tmp_all.upper_distance_max < tval) {
          tmp_all.upper_distance_max = tval;
        }
        tval = Math.min(...tmp_lower_distance);
        if (tval < tmp_all.lower_distance_min) {
          tmp_all.lower_distance_min = tval;
        }
        tval = Math.max(...tmp_lower_distance);
        if (tmp_all.lower_distance_max < tval) {
          tmp_all.lower_distance_max = tval;
        }
        tval = Math.min(...tmp_lower_limits);
        if (tval < tmp_all.limits_min) {
          tmp_all.limits_min = tval;
        }
        tval = Math.max(...tmp_upper_limits);
        if (tmp_all.limits_max < tval) {
          tmp_all.limits_max = tval;
        }
      });
    }

    tmp_all.map = listData[0]?.map || '';

    const upperLimits = new Set(listData.map((item: {upper_limit: any}) => item.upper_limit));
    const lowerLimits = new Set(listData.map((item: {lower_limit: any}) => item.lower_limit));
    tmp_all.hasMultipleLimit = upperLimits.size > 1 || lowerLimits.size > 1;
    setGraphBaseData(tmp_all);
  }, [listData]);

  return searchConditions ? (
    <ProgressAria startProcess={startProcess} loadingMessage>
      <Box style={{float: 'right'}}>
        <Button
          variant="contained"
          size={'small'}
          color="secondary"
          startIcon={<DescriptionIcon />}
          onClick={() => setDialogOpen(true)}
        >
          {t('データ一覧')}
        </Button>
      </Box>

      <CloudDistanceMaxMinComparison
        searchConditions={searchConditions}
        graphBaseData={graphBaseData}
        listData={listData}
      />
      <Divider sx={{margin: '0.5em 0'}} />

      <CloudDistanceMaxAnalysis
        searchConditions={searchConditions}
        graphBaseData={graphBaseData}
        listData={listData}
      />
      <Divider sx={{margin: '0.5em 0'}} />

      <CloudDistanceMinAnalysis
        searchConditions={searchConditions}
        graphBaseData={graphBaseData}
        listData={listData}
      />

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth={false} fullWidth>
        <LFCDialogTitle onClose={handleClose}>{`${t('menu.page_name.Cloud Distance')}：${t(
          '詳細データ'
        )}`}</LFCDialogTitle>
        <DialogContent>
          <LFCDataGridSimple
            columns={CloudDistanceDetailColumn}
            rows={listData}
            height="80vh"
            exportFilename={`Cloud Distance_${t('詳細データ')}`}
          />
        </DialogContent>
      </Dialog>
    </ProgressAria>
  ) : (
    <Box height={'50vh'}></Box>
  );
};
